import { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Download, Edit, ArrowLeft, User, Car, FileText, Check, X, Clock, Camera } from 'lucide-react';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import CheckinForm from '../components/checkin/CheckinForm';
import CheckinReceipt from '../components/checkin/CheckinReceipt';
import QuoteForm from '../components/quotes/QuoteForm';
import VehicleTimeline from '../components/timeline/VehicleTimeline';
import { currency, date, distance, distanceUnit, getFormatterLocalization } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { getOficinaId } from '../lib/office';
import { SERVICE_ORDER_STATUSES, serviceOrderStatusLabel, canTransitionStatus } from '../lib/serviceOrders';

async function imageUrlToDataUrl(url) {
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Logo indisponivel');
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function DetalhesOS() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { perfil } = useAuth();
  const { features: planFeatures } = usePlanFeatures();
  const canViewFinance = perfil?.perfil === 'admin';
  const [loading, setLoading] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingLembrete, setSavingLembrete] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [checkinCompleted, setCheckinCompleted] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [os, setOs] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [pecas, setPecas] = useState([]);
  const [editForm, setEditForm] = useState({
    status: 'aberta',
    problema_relatorio: '',
    km_entrada: '',
    data_previsao: '',
    lembrete_contato_em: '',
    lembrete_contato_obs: '',
    lembrete_contato_concluido_em: null,
    data_saida: '',
    forma_pagamento: '',
    observacoes: '',
  });

  const isEditing = location.pathname.endsWith('/edit');
  const canCompleteOS = ['admin', 'mecanico', 'super_admin', 'gdn_admin'].includes(String(perfil?.perfil || ''));
  const requireFeature = (feature, label) => {
    if (planFeatures[feature]) return true;
    toast.error(`${label} está disponível a partir do plano Pro.`);
    return false;
  };

  useEffect(() => {
    if (!id) return;
    loadOS();
    // loadOS is scoped to the route id and must not retrigger on local form changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOS = async () => {
    try {
      setLoading(true);
      const oficinaId = await getOficinaId();
      if (!oficinaId) return;
      
      // Carregar OS principal
      const { data: osData, error: osError } = await supabase
        .from('ordens_servico')
        .select(`
          *,
          clientes!inner(nome, telefone, email, cpf, cep, rua, numero, bairro, cidade, estado),
          veiculos!inner(marca, modelo, placa, ano, cor),
          usuarios(nome)
        `)
        .eq('id', id)
        .eq('oficina_id', oficinaId)
        .single();

      if (osError) throw osError;

      // Carregar serviços da OS
      const { data: servicosData } = await supabase
        .from('os_servicos')
        .select('*')
        .eq('os_id', id);

      // Carregar peças da OS
      const { data: pecasData } = await supabase
        .from('os_pecas')
        .select(`
          *,
          estoque!inner(nome)
        `)
        .eq('os_id', id);

      setOs(osData);
      setEditForm({
        status: osData.status || 'aberta',
        problema_relatorio: osData.problema_relatorio || '',
        km_entrada: osData.km_entrada ? String(osData.km_entrada) : '',
        data_previsao: osData.data_previsao ? String(osData.data_previsao).slice(0, 10) : '',
        lembrete_contato_em: osData.lembrete_contato_em ? String(osData.lembrete_contato_em).slice(0, 10) : '',
        lembrete_contato_obs: osData.lembrete_contato_obs || '',
        lembrete_contato_concluido_em: osData.lembrete_contato_concluido_em || null,
        data_saida: osData.data_saida ? String(osData.data_saida).slice(0, 10) : '',
        forma_pagamento: osData.forma_pagamento || '',
        observacoes: osData.observacoes || '',
      });
      setServicos(servicosData || []);
      setPecas(pecasData || []);
    } catch (error) {
      console.error('Error loading OS:', error);
      toast.error('Erro ao carregar ordem de serviço');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aberta': return 'bg-blue-100 text-blue-800';
      case 'diagnostico': return 'bg-cyan-100 text-cyan-800';
      case 'aguardando_aprovacao': return 'bg-amber-100 text-amber-800';
      case 'em_execucao': return 'bg-yellow-100 text-yellow-800';
      case 'aguardando_peca': return 'bg-purple-100 text-purple-800';
      case 'finalizada':
      case 'entregue': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      case 'atrasada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aberta': return <FileText size={16} />;
      case 'diagnostico':
      case 'aguardando_aprovacao':
      case 'em_execucao':
      case 'aguardando_peca': return <Clock size={16} />;
      case 'finalizada':
      case 'entregue': return <Check size={16} />;
      case 'cancelada': return <X size={16} />;
      default: return <FileText size={16} />;
    }
  };

  const calcularTotal = () => {
    const totalServicos = servicos.reduce((acc, servico) => acc + (servico.valor || 0), 0);
    const totalPecas = pecas.reduce((acc, peca) => acc + ((peca.valor || 0) * (peca.quantidade || 0)), 0);
    return totalServicos + totalPecas;
  };

  const normalizeStatus = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const isStatusEntregue = (value) => ['finalizada', 'concluida', 'entregue'].includes(normalizeStatus(value));

  // eslint-disable-next-line no-unused-vars
  const gerarPDFLegacy = async () => {
    const toastId = toast.loading('Gerando PDF profissional...');
    try {
      if (!os) {
        throw new Error('Ordem de serviço ainda não carregada');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Função auxiliar para adicionar texto
      const addText = (text, x, y, fontSize = 12, isBold = false, options = {}) => {
        pdf.setFontSize(fontSize);
        pdf.setFont(isBold ? 'helvetica' : 'helvetica', isBold ? 'bold' : 'normal');
        pdf.text(String(text ?? '-'), x, y, options);
      };

      // Função para adicionar linha
      const addLine = (x1, y1, x2, y2) => {
        pdf.setLineWidth(0.5);
        pdf.line(x1, y1, x2, y2);
      };

      // Cabeçalho
      pdf.setFillColor(41, 128, 185);
      pdf.rect(0, 0, pageWidth, 40);
      pdf.setTextColor(255, 255, 255);
      addText('MecSync', pageWidth / 2, 15, 24, true, { align: 'center' });
      addText('Sistema de Gestão de Oficinas', pageWidth / 2, 25, 12, false, { align: 'center' });
      addText('Ordem de Serviço', pageWidth / 2, 32, 10, false, { align: 'center' });
      
      pdf.setTextColor(0, 0, 0);
      yPosition = 50;

      // Número da OS e Data
      addText(`OS #${os.numero || '-'}`, 20, yPosition, 18, true);
      addText(`Data: ${date(os.data_entrada)}`, pageWidth - 60, yPosition, 12, false);
      yPosition += 15;

      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;

      // Seção Cliente e Veículo lado a lado
      const colWidth = (pageWidth - 60) / 2;
      
      // Cliente
      addText('DADOS DO CLIENTE', 20, yPosition, 12, true);
      yPosition += 8;
      addText(`Nome: ${os.clientes?.nome || '-'}`, 20, yPosition, 10);
      yPosition += 6;
      addText(`Telefone: ${os.clientes?.telefone || 'Não informado'}`, 20, yPosition, 10);
      yPosition += 6;
      addText(`Email: ${os.clientes?.email || 'Não informado'}`, 20, yPosition, 10);
      yPosition += 6;
      if (os.clientes?.cpf) {
        addText(`CPF: ${os.clientes.cpf}`, 20, yPosition, 10);
        yPosition += 6;
      }
      if (os.clientes?.rua) {
        const endereco = `${os.clientes.rua}, ${os.clientes.numero}${os.clientes.bairro ? ', ' + os.clientes.bairro : ''}${os.clientes.cidade ? ', ' + os.clientes.cidade : ''}${os.clientes.estado ? ' - ' + os.clientes.estado : ''}`;
        addText(`Endereço: ${endereco}`, 20, yPosition, 10);
        yPosition += 6;
      }

      // Veículo
      yPosition = 78; // Reset para alinhar com o início do cliente
      addText('DADOS DO VEÍCULO', 20 + colWidth + 20, yPosition, 12, true);
      yPosition += 8;
      addText(`Marca: ${os.veiculos?.marca || '-'}`, 20 + colWidth + 20, yPosition, 10);
      yPosition += 6;
      addText(`Modelo: ${os.veiculos?.modelo || '-'}`, 20 + colWidth + 20, yPosition, 10);
      yPosition += 6;
      addText(`Placa: ${os.veiculos?.placa || '-'}`, 20 + colWidth + 20, yPosition, 10);
      yPosition += 6;
      addText(`Ano: ${os.veiculos?.ano || '-'}`, 20 + colWidth + 20, yPosition, 10);
      yPosition += 6;
      addText(`Cor: ${os.veiculos?.cor || '-'}`, 20 + colWidth + 20, yPosition, 10);
      yPosition += 6;
      if (os.km_entrada) {
        addText(`Odometro: ${distance(os.km_entrada)}`, 20 + colWidth + 20, yPosition, 10);
        yPosition += 6;
      }

      yPosition = Math.max(yPosition, 130) + 10;
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;

      // Status e Mecânico
      addText('STATUS E RESPONSÁVEL', 20, yPosition, 12, true);
      yPosition += 8;
      
      const statusText = String(os.status || '-').replaceAll('_', ' ').toUpperCase();
      addText(`Status: ${statusText}`, 20, yPosition, 10);
      yPosition += 6;
      
      if (os.data_previsao) {
        addText(`Previsão: ${date(os.data_previsao)}`, 20, yPosition, 10);
        yPosition += 6;
      }
      
      addText(`Mecânico: ${os.usuarios ? os.usuarios.nome : 'Não definido'}`, 20, yPosition, 10);
      yPosition += 10;

      // Problema Relatado
      if (os.problema_relatorio) {
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 10;
        addText('PROBLEMA RELATADO', 20, yPosition, 12, true);
        yPosition += 8;
        
        const problemaLines = pdf.splitTextToSize(os.problema_relatorio, pageWidth - 40);
        problemaLines.forEach(line => {
          addText(line, 20, yPosition, 10);
          yPosition += 6;
        });
        yPosition += 5;
      }

      // Serviços
      if (servicos.length > 0) {
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 10;
        addText('SERVIÇOS REALIZADOS', 20, yPosition, 12, true);
        yPosition += 8;

        // Cabeçalho da tabela de serviços
        addText('Serviço', 20, yPosition, 10, true);
        addText(`Valor (${getFormatterLocalization().currency})`, pageWidth - 60, yPosition, 10, true);
        yPosition += 6;
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 6;

        // Lista de serviços
        servicos.forEach((servico, index) => {
          const servicoLines = pdf.splitTextToSize(servico.descricao || '', pageWidth - 100);
          servicoLines.forEach((line, i) => {
            addText(line, 20, yPosition, 9);
            if (i === 0) {
              addText(currency(servico.valor || 0), pageWidth - 60, yPosition, 9);
            }
            yPosition += 5;
          });
        });
        
        yPosition += 5;
      }

      // Peças
      if (pecas.length > 0) {
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 10;
        addText('PEÇAS UTILIZADAS', 20, yPosition, 12, true);
        yPosition += 8;

        // Cabeçalho da tabela de peças
        addText('Peça', 20, yPosition, 10, true);
        addText('Qtd', pageWidth - 90, yPosition, 10, true);
        addText('Unit.', pageWidth - 60, yPosition, 10, true);
        addText('Total', pageWidth - 30, yPosition, 10, true);
        yPosition += 6;
        addLine(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 6;

        // Lista de peças
        pecas.forEach((peca, index) => {
          const pecaLines = pdf.splitTextToSize(peca.estoque?.nome || '', pageWidth - 120);
          pecaLines.forEach((line, i) => {
            addText(line, 20, yPosition, 9);
            if (i === 0) {
              addText(String(peca.quantidade || 0), pageWidth - 90, yPosition, 9);
              addText(currency(peca.valor || 0), pageWidth - 60, yPosition, 9);
              addText(currency((peca.valor || 0) * (peca.quantidade || 0)), pageWidth - 30, yPosition, 9);
            }
            yPosition += 5;
          });
        });
        
        yPosition += 5;
      }

      // Total
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;
      
      pdf.setFillColor(41, 128, 185);
      pdf.rect(pageWidth - 80, yPosition - 5, 60, 15, 'F');
      pdf.setTextColor(255, 255, 255);
      addText('TOTAL:', pageWidth - 75, yPosition + 3, 12, true);
      addText(currency(calcularTotal()), pageWidth - 30, yPosition + 3, 12, true);
      
      pdf.setTextColor(0, 0, 0);
      yPosition += 20;

      // Observações
      if (os.observacoes) {
        addText('OBSERVAÇÕES', 20, yPosition, 12, true);
        yPosition += 8;
        
        const obsLines = pdf.splitTextToSize(os.observacoes, pageWidth - 40);
        obsLines.forEach(line => {
          addText(line, 20, yPosition, 10);
          yPosition += 6;
        });
        yPosition += 10;
      }

      // Rodapé
      const footerY = pageHeight - 30;
      addLine(20, footerY, pageWidth - 20, footerY);
      
      pdf.setTextColor(100, 100, 100);
      addText('Desenvolvido por GDN Corp', pageWidth / 2, footerY + 10, 9, false, { align: 'center' });
      addText('www.gdncorp.com.br', pageWidth / 2, footerY + 18, 9, false, { align: 'center' });
      
      // Adicionar página de termos e condições
      if (yPosition > pageHeight - 50) {
        pdf.addPage();
      }
      
      pdf.addPage();
      yPosition = 30;
      
      addText('TERMOS E CONDIÇÕES', pageWidth / 2, yPosition, 16, true, { align: 'center' });
      yPosition += 20;
      
      const termos = [
        '1. A execução dos serviços está sujeita à disponibilidade de peças e mão de obra.',
        '2. Os prazos de entrega são estimados e poderão ser alterados mediante aviso prévio.',
        '3. Peças e serviços não autorizados expressamente pelo cliente não serão cobrados.',
        '4. A oficina não se responsabiliza por objetos pessoais deixados no veículo.',
        '5. O veículo só será liberado após quitação total dos serviços.',
        '6. Garantia de 90 dias para peças e mão de obra, exceto por desgaste natural.',
        '7. A oficina se reserva o direito de retenção do veículo em caso de não pagamento.'
      ];
      
      termos.forEach(termo => {
        const termoLines = pdf.splitTextToSize(termo, pageWidth - 40);
        termoLines.forEach(line => {
          addText(line, 20, yPosition, 10);
          yPosition += 6;
        });
        yPosition += 4;
      });

      yPosition += 20;
      addLine(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 15;
      
      addText('Assinatura do Cliente', pageWidth / 2, yPosition, 12, true, { align: 'center' });
      yPosition += 30;
      addLine(pageWidth / 2 - 40, yPosition, pageWidth / 2 + 40, yPosition);
      yPosition += 10;
      addText('_________________________________________', pageWidth / 2, yPosition, 10, false, { align: 'center' });
      yPosition += 8;
      addText('Assinatura', pageWidth / 2, yPosition, 10, true, { align: 'center' });
      
      const fileName = `OS-${os.numero || 'sem-numero'}-MecSync.pdf`;
      const blobUrl = URL.createObjectURL(pdf.output('blob'));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success('PDF profissional gerado com sucesso!', { id: toastId });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.message || 'Erro ao gerar PDF', { id: toastId });
    }
  };

  const gerarPDF = async () => {
    const toastId = toast.loading('Gerando PDF profissional...');
    try {
      if (!os) throw new Error('Ordem de serviço ainda não carregada');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;
      const bottomLimit = pageHeight - 18;
      let y = 18;
      const footedPages = new Set();
      const oficinaId = await getOficinaId();
      const { data: oficina } = oficinaId
        ? await supabase.from('oficinas').select('nome,logo').eq('id', oficinaId).maybeSingle()
        : { data: null };
      const officeLogo = oficina?.logo ? await imageUrlToDataUrl(oficina.logo).catch(() => null) : null;
      const officeName = oficina?.nome || 'MecSync';
      const logoFormat = String(officeLogo || '').includes('image/png') ? 'PNG' : 'JPEG';

      const setFont = (size = 10, style = 'normal', color = [30, 41, 59]) => {
        pdf.setFont('helvetica', style);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
      };
      const addFooter = () => {
        const page = pdf.internal.getCurrentPageInfo().pageNumber;
        if (footedPages.has(page)) return;
        footedPages.add(page);
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
        setFont(8, 'normal', [100, 116, 139]);
        pdf.text('Desenvolvido por GDN Corp - www.gdncorp.com.br', pageWidth / 2, pageHeight - 8, { align: 'center' });
      };
      const addHeader = (firstPage = false) => {
        if (firstPage) {
          pdf.setFillColor(15, 23, 42);
          pdf.rect(0, 0, pageWidth, 38, 'F');
          pdf.setFillColor(249, 115, 22);
          pdf.rect(0, 0, pageWidth, 3, 'F');
          if (officeLogo) {
            pdf.addImage(officeLogo, logoFormat, margin, 7, 26, 18, undefined, 'FAST');
            setFont(14, 'bold', [255, 255, 255]);
            pdf.text(officeName, margin + 31, 16);
          } else {
            setFont(20, 'bold', [255, 255, 255]);
            pdf.text(officeName, margin, 17);
          }
          setFont(9, 'normal', [203, 213, 225]);
          pdf.text('Documento gerado pelo MecSync', officeLogo ? margin + 31 : margin, 25);
          setFont(16, 'bold', [255, 255, 255]);
          pdf.text('Ordem de Serviço', pageWidth - margin, 17, { align: 'right' });
          setFont(10, 'normal', [226, 232, 240]);
          pdf.text(`#${os.numero || '-'}`, pageWidth - margin, 25, { align: 'right' });
          y = 48;
        } else {
          setFont(10, 'bold', [15, 23, 42]);
          pdf.text(`OS #${os.numero || '-'}`, margin, 10);
          pdf.setDrawColor(226, 232, 240);
          pdf.line(margin, 13, pageWidth - margin, 13);
          y = 22;
        }
      };
      const newPage = () => {
        addFooter();
        pdf.addPage();
        addHeader(false);
      };
      const ensureSpace = (height) => {
        if (y + height > bottomLimit) newPage();
      };
      const addSectionTitle = (title) => {
        ensureSpace(13);
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, y - 5, contentWidth, 9, 'F');
        setFont(10, 'bold', [15, 23, 42]);
        pdf.text(title, margin + 3, y + 1);
        y += 12;
      };
      const addWrappedText = (text, x = margin, width = contentWidth, size = 9, lineHeight = 5) => {
        const lines = pdf.splitTextToSize(String(text || '-'), width);
        lines.forEach((line) => {
          ensureSpace(lineHeight + 1);
          setFont(size);
          pdf.text(line, x, y);
          y += lineHeight;
        });
      };
      const addInfoRow = (label, value, x, width) => {
        const labelWidth = 24;
        const lines = pdf.splitTextToSize(String(value || '-'), width - labelWidth);
        ensureSpace(Math.max(6, lines.length * 5));
        setFont(8, 'bold', [71, 85, 105]);
        pdf.text(`${label}:`, x, y);
        setFont(9);
        pdf.text(lines, x + labelWidth, y);
        y += Math.max(6, lines.length * 5);
      };
      const addTableHeader = (columns) => {
        ensureSpace(10);
        pdf.setFillColor(15, 23, 42);
        pdf.rect(margin, y - 5, contentWidth, 9, 'F');
        setFont(8, 'bold', [255, 255, 255]);
        columns.forEach((col) => pdf.text(col.label, col.x, y, col.options || {}));
        y += 9;
      };

      addHeader(true);

      const entrada = date(os.data_entrada);
      const previsao = os.data_previsao ? date(os.data_previsao) : 'Não definida';
      const endereco = os.clientes?.rua
        ? `${os.clientes.rua}, ${os.clientes.numero || 's/n'}${os.clientes.bairro ? `, ${os.clientes.bairro}` : ''}${os.clientes.cidade ? `, ${os.clientes.cidade}` : ''}${os.clientes.estado ? ` - ${os.clientes.estado}` : ''}`
        : 'Não informado';
      const leftX = margin;
      const rightX = margin + contentWidth / 2 + 5;
      const colWidth = contentWidth / 2 - 7;

      addSectionTitle('Dados gerais');
      const startGeneralY = y;
      addInfoRow('OS', os.numero || '-', leftX, colWidth);
      addInfoRow('Entrada', entrada, leftX, colWidth);
      addInfoRow('Status', String(os.status || '-').replaceAll('_', ' ').toUpperCase(), leftX, colWidth);
      addInfoRow('Previsão', previsao, leftX, colWidth);
      const leftGeneralEnd = y;
      y = startGeneralY;
      addInfoRow('Cliente', os.clientes?.nome || '-', rightX, colWidth);
      addInfoRow('Telefone', os.clientes?.telefone || 'Não informado', rightX, colWidth);
      addInfoRow('Email', os.clientes?.email || 'Não informado', rightX, colWidth);
      addInfoRow('Endereço', endereco, rightX, colWidth);
      y = Math.max(y, leftGeneralEnd) + 4;

      addSectionTitle('Veículo e responsável');
      const startVehicleY = y;
      addInfoRow('Marca', os.veiculos?.marca || '-', leftX, colWidth);
      addInfoRow('Modelo', os.veiculos?.modelo || '-', leftX, colWidth);
      addInfoRow('Placa', os.veiculos?.placa || '-', leftX, colWidth);
      addInfoRow('Ano', os.veiculos?.ano || '-', leftX, colWidth);
      const leftVehicleEnd = y;
      y = startVehicleY;
      addInfoRow('Cor', os.veiculos?.cor || '-', rightX, colWidth);
      addInfoRow(`Odometro (${distanceUnit()})`, os.km_entrada ? distance(os.km_entrada) : 'Não informado', rightX, colWidth);
      addInfoRow('Mecânico', os.usuarios?.nome || 'Não definido', rightX, colWidth);
      y = Math.max(y, leftVehicleEnd) + 4;

      if (os.problema_relatorio) {
        addSectionTitle('Problema relatado');
        addWrappedText(os.problema_relatorio);
        y += 4;
      }

      if (servicos.length > 0) {
        addSectionTitle('Serviços');
        addTableHeader([
          { label: 'Descrição', x: margin + 3 },
          { label: 'Valor', x: pageWidth - margin - 3, options: { align: 'right' } },
        ]);
        servicos.forEach((servico) => {
          const descLines = pdf.splitTextToSize(servico.descricao || 'Serviço', contentWidth - 42);
          const rowHeight = Math.max(8, descLines.length * 5 + 3);
          ensureSpace(rowHeight);
          setFont(9);
          pdf.text(descLines, margin + 3, y);
          setFont(9, 'bold');
          pdf.text(currency(servico.valor || 0), pageWidth - margin - 3, y, { align: 'right' });
          y += rowHeight;
          pdf.setDrawColor(226, 232, 240);
          pdf.line(margin, y - 2, pageWidth - margin, y - 2);
        });
        y += 3;
      }

      if (pecas.length > 0) {
        addSectionTitle('Peças utilizadas');
        addTableHeader([
          { label: 'Peça', x: margin + 3 },
          { label: 'Qtd', x: pageWidth - 75, options: { align: 'right' } },
          { label: 'Unit.', x: pageWidth - 45, options: { align: 'right' } },
          { label: 'Total', x: pageWidth - margin - 3, options: { align: 'right' } },
        ]);
        pecas.forEach((peca) => {
          const nameLines = pdf.splitTextToSize(peca.estoque?.nome || 'Peça', contentWidth - 90);
          const rowHeight = Math.max(8, nameLines.length * 5 + 3);
          ensureSpace(rowHeight);
          setFont(9);
          pdf.text(nameLines, margin + 3, y);
          pdf.text(String(peca.quantidade || 0), pageWidth - 75, y, { align: 'right' });
          pdf.text(currency(peca.valor || 0), pageWidth - 45, y, { align: 'right' });
          setFont(9, 'bold');
          pdf.text(currency((peca.valor || 0) * (peca.quantidade || 0)), pageWidth - margin - 3, y, { align: 'right' });
          y += rowHeight;
          pdf.setDrawColor(226, 232, 240);
          pdf.line(margin, y - 2, pageWidth - margin, y - 2);
        });
        y += 3;
      }

      ensureSpace(20);
      pdf.setFillColor(249, 115, 22);
      pdf.rect(pageWidth - margin - 70, y - 4, 70, 14, 'F');
      setFont(11, 'bold', [255, 255, 255]);
      pdf.text('TOTAL', pageWidth - margin - 66, y + 5);
      pdf.text(currency(calcularTotal()), pageWidth - margin - 4, y + 5, { align: 'right' });
      y += 20;

      if (os.observacoes) {
        addSectionTitle('Observações');
        addWrappedText(os.observacoes);
        y += 4;
      }

      newPage();
      addSectionTitle('Termos e condições');
      [
        '1. A execução dos serviços está sujeita à disponibilidade de peças e mão de obra.',
        '2. Os prazos de entrega são estimados e poderão ser alterados mediante aviso prévio.',
        '3. Peças e serviços não autorizados expressamente pelo cliente não serão cobrados.',
        '4. A oficina não se responsabiliza por objetos pessoais deixados no veículo.',
        '5. O veículo só será liberado após quitação total dos serviços.',
        '6. Garantia de 90 dias para peças e mão de obra, exceto por desgaste natural.',
        '7. A oficina se reserva o direito de retenção do veículo em caso de não pagamento.',
      ].forEach((termo) => {
        addWrappedText(termo, margin, contentWidth, 9, 5);
        y += 3;
      });

      ensureSpace(42);
      y += 18;
      pdf.setDrawColor(100, 116, 139);
      pdf.line(pageWidth / 2 - 45, y, pageWidth / 2 + 45, y);
      y += 7;
      setFont(10, 'bold');
      pdf.text('Assinatura do Cliente', pageWidth / 2, y, { align: 'center' });

      addFooter();
      pdf.save(`OS-${os.numero || 'sem-numero'}-MecSync.pdf`);
      toast.success('PDF profissional gerado com sucesso!', { id: toastId });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.message || 'Erro ao gerar PDF', { id: toastId });
    }
  };

  const salvarEdicao = async () => {
    try {
      setSavingEdit(true);
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não identificada');

      if (String(os?.status || '') !== String(editForm.status)
        && !canTransitionStatus(String(os?.status || ''), String(editForm.status))) {
        throw new Error('Transição de status não permitida para esta OS.');
      }

      const payload = {
        status: editForm.status,
        problema_relatorio: editForm.problema_relatorio.trim() || null,
        km_entrada: editForm.km_entrada ? Number(editForm.km_entrada) : null,
        data_previsao: editForm.data_previsao || null,
        data_saida: isStatusEntregue(editForm.status)
          ? new Date(`${editForm.data_saida || new Date().toISOString().slice(0, 10)}T12:00:00`).toISOString()
          : null,
        lembrete_contato_em: editForm.lembrete_contato_em || null,
        lembrete_contato_obs: editForm.lembrete_contato_obs.trim() || null,
        observacoes: editForm.observacoes.trim() || null,
      };

      if (isStatusEntregue(editForm.status) && !canCompleteOS) {
        throw new Error('Apenas admin ou mecânico pode concluir uma OS.');
      }

      if (canViewFinance) {
        payload.forma_pagamento = editForm.forma_pagamento || null;
      }

      const { error } = await supabase
        .from('ordens_servico')
        .update(payload)
        .eq('id', id)
        .eq('oficina_id', oficinaId);

      if (error) throw error;
      await loadOS();
      toast.success('OS atualizada com sucesso');
      navigate(`/ordens/${id}`, { replace: true });
    } catch (error) {
      console.error('Erro ao editar OS:', error);
      toast.error(error.message || 'Não foi possível editar a OS');
    } finally {
      setSavingEdit(false);
    }
  };

  const concluirLembreteContato = async () => {
    if (savingEdit || savingLembrete) return;
    try {
      setSavingLembrete(true);
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não identificada');
      const { error } = await supabase
        .from('ordens_servico')
        .update({ lembrete_contato_concluido_em: new Date().toISOString() })
        .eq('id', id)
        .eq('oficina_id', oficinaId);
      if (error) throw error;
      toast.success('Lembrete marcado como concluído');
      await loadOS();
    } catch (error) {
      toast.error(error.message || 'Não foi possível concluir o lembrete');
    } finally {
      setSavingLembrete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Carregando ordem de serviço...</div>
      </div>
    );
  }

  if (!os) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400">Ordem de serviço não encontrada</p>
        <Button onClick={() => navigate('/ordens')} className="mt-4">
          Voltar para Ordens
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Ordem de Serviço #${os.numero}`}
        subtitle={isEditing ? 'Edite os dados principais da OS' : 'Detalhes completos da OS'}
        actions={
          <div className="flex gap-2">
            <Button onClick={() => requireFeature('checkin', 'Check-in digital') && setShowCheckinModal(true)}>
              <Camera size={16} className="mr-2" />
              Check-in Digital
            </Button>
            <Button onClick={() => requireFeature('quotes', 'Orçamento digital') && setShowQuoteModal(true)}>
              <FileText size={16} className="mr-2" />
              Orçamento Digital
            </Button>
            {canViewFinance && (
              <Button onClick={gerarPDF}>
                <Download size={16} className="mr-2" />
                Gerar PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/ordens/${id}/edit`)}>
              <Edit size={16} className="mr-2" />
              Editar
            </Button>
          </div>
        }
      />

      {isEditing && (
        <Card className="space-y-4 p-4">
          <div>
            <h3 className="font-semibold">Editar ordem de serviço</h3>
            <p className="text-sm text-zinc-400">Atualize status, problema, previsão e observações.</p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Status</label>
              <select
                className="input-base"
                value={editForm.status}
                onChange={(event) => {
                  if (isStatusEntregue(event.target.value) && !canCompleteOS) {
                    toast.error('Apenas admin ou mecânico pode concluir uma OS.');
                    return;
                  }
                  setEditForm((prev) => ({
                    ...prev,
                    status: event.target.value,
                    data_saida: isStatusEntregue(event.target.value) && !prev.data_saida
                      ? new Date().toISOString().slice(0, 10)
                      : prev.data_saida,
                  }));
                }}
              >
                {SERVICE_ORDER_STATUSES.map((status) => (
                  <option
                    key={status.value}
                    value={status.value}
                    disabled={['finalizada', 'entregue'].includes(status.value) && !canCompleteOS}
                  >
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {isStatusEntregue(editForm.status) && (
              <div>
                <label className="mb-1 block text-sm font-medium">Data de conclusao</label>
                <Input
                  type="date"
                  value={editForm.data_saida || new Date().toISOString().slice(0, 10)}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, data_saida: event.target.value }))}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium">KM entrada</label>
              <Input type="number" value={editForm.km_entrada} onChange={(event) => setEditForm((prev) => ({ ...prev, km_entrada: event.target.value }))} />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Previsão de entrega</label>
              <Input type="date" value={editForm.data_previsao} onChange={(event) => setEditForm((prev) => ({ ...prev, data_previsao: event.target.value }))} />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Lembrete de contato (opcional)</label>
              <Input
                type="date"
                placeholder="Opcional: escolha a data do lembrete"
                aria-label="Data opcional para lembrar o admin de contatar o cliente"
                title="Opcional: escolha quando o sistema deve lembrar o admin de contatar o cliente."
                value={editForm.lembrete_contato_em}
                onChange={(event) => setEditForm((prev) => ({ ...prev, lembrete_contato_em: event.target.value }))}
              />
              <p className="mt-1 text-xs text-zinc-500">Opcional. Use para lembrar o admin de falar com o cliente depois de uma revisão, entrega ou retorno combinado.</p>
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">Observação do lembrete (opcional)</label>
              <Input placeholder="Opcional: ex. ligar 30 dias após a revisão para oferecer retorno" value={editForm.lembrete_contato_obs} onChange={(event) => setEditForm((prev) => ({ ...prev, lembrete_contato_obs: event.target.value }))} />
            </div>

            {canViewFinance && <div>
              <label className="mb-1 block text-sm font-medium">Forma de pagamento</label>
              <select className="input-base" value={editForm.forma_pagamento} onChange={(event) => setEditForm((prev) => ({ ...prev, forma_pagamento: event.target.value }))}>
                <option value="">Não definido</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="cartao_credito">Cartão de Crédito</option>
                <option value="cartao_debito">Cartão de Débito</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="misto">Misto</option>
              </select>
            </div>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Problema relatado</label>
            <textarea className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none" rows={4} value={editForm.problema_relatorio} onChange={(event) => setEditForm((prev) => ({ ...prev, problema_relatorio: event.target.value }))} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Observações internas</label>
            <textarea className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none" rows={3} value={editForm.observacoes} onChange={(event) => setEditForm((prev) => ({ ...prev, observacoes: event.target.value }))} />
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate(`/ordens/${id}`)} disabled={savingEdit}>Cancelar</Button>
            <Button onClick={salvarEdicao} disabled={savingEdit}>{savingEdit ? 'Salvando...' : 'Salvar alterações'}</Button>
          </div>
        </Card>
      )}

      <div id="os-pdf-content" className="bg-white p-8">
        {/* Cabeçalho do PDF */}
        <div className="text-center mb-6 border-b border-gray-200 pb-4">
          <div className="flex justify-center">
            <div className="font-display text-2xl font-black leading-none">
              <span className="text-zinc-950">Mec</span>
              <span className="text-orange-500">Sync</span>
            </div>
          </div>
          <p className="text-gray-600">Ordem de Serviço #{os.numero}</p>
          <p className="text-sm text-gray-500">
            {date(os.data_entrada)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Dados do Cliente */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center">
              <User size={16} className="mr-2" />
              Dados do Cliente
            </h3>
            <div className="space-y-2 text-sm">
              <div><strong>Nome:</strong> {os.clientes.nome}</div>
              <div><strong>Telefone:</strong> {os.clientes.telefone}</div>
              <div><strong>Email:</strong> {os.clientes.email}</div>
              {os.clientes.cpf && <div><strong>CPF:</strong> {os.clientes.cpf}</div>}
              {os.clientes.rua && (
                <div>
                  <strong>Endereço:</strong> {os.clientes.rua}, {os.clientes.numero}
                  {os.clientes.bairro && `, ${os.clientes.bairro}`}
                  {os.clientes.cidade && `, ${os.clientes.cidade}`}
                  {os.clientes.estado && ` - ${os.clientes.estado}`}
                </div>
              )}
            </div>
          </Card>

          {/* Dados do Veículo */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3 flex items-center">
              <Car size={16} className="mr-2" />
              Dados do Veículo
            </h3>
            <div className="space-y-2 text-sm">
              <div><strong>Marca:</strong> {os.veiculos.marca}</div>
              <div><strong>Modelo:</strong> {os.veiculos.modelo}</div>
              <div><strong>Placa:</strong> {os.veiculos.placa}</div>
              <div><strong>Ano:</strong> {os.veiculos.ano}</div>
              <div><strong>Cor:</strong> {os.veiculos.cor}</div>
              {os.km_entrada && <div><strong>Odometro:</strong> {distance(os.km_entrada)}</div>}
            </div>
          </Card>
        </div>

        {/* Status e Mecânico */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Status</h3>
            <div className="flex items-center gap-2">
              {getStatusIcon(os.status)}
              <Badge className={getStatusColor(os.status)}>
                {serviceOrderStatusLabel(os.status).toUpperCase()}
              </Badge>
            </div>
            {os.data_previsao && (
              <div className="mt-2 text-sm">
                <strong>Previsão de Entrega:</strong>{' '}
                {date(os.data_previsao)}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <h3 className="font-semibold mb-3">Mecânico Responsável</h3>
            <div className="text-sm">
              {os.usuarios ? os.usuarios.nome : 'Não definido'}
            </div>
          </Card>
        </div>

        {os.lembrete_contato_em && (
          <Card className="p-4 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-semibold mb-2">Lembrete de contato com cliente</h3>
                <p className="text-sm text-gray-700">
                  Entrar em contato em {date(os.lembrete_contato_em)}
                </p>
                {os.lembrete_contato_obs && <p className="mt-1 text-sm text-gray-600">{os.lembrete_contato_obs}</p>}
                {os.lembrete_contato_concluido_em && (
                  <p className="mt-2 text-xs text-green-700">Concluído em {date(os.lembrete_contato_concluido_em)}</p>
                )}
              </div>
              {!os.lembrete_contato_concluido_em && (
                <Button variant="ghost" onClick={concluirLembreteContato}>
                  Marcar como contatado
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Problema Relatado */}
        {os.problema_relatorio && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3">Problema Relatado</h3>
            <p className="text-sm text-gray-700">{os.problema_relatorio}</p>
          </Card>
        )}

        {/* Serviços Realizados */}
        {servicos.length > 0 && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3">Serviços Realizados</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Serviço</th>
                    {canViewFinance && <th className="text-right py-2">Valor</th>}
                  </tr>
                </thead>
                <tbody>
                  {servicos.map((servico, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{servico.descricao}</td>
                      {canViewFinance && <td className="text-right py-2">{currency(servico.valor || 0)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Peças Utilizadas */}
        {pecas.length > 0 && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3">Peças Utilizadas</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Peça</th>
                    <th className="text-center py-2">Qtd</th>
                    {canViewFinance && <th className="text-right py-2">Valor Unit.</th>}
                    {canViewFinance && <th className="text-right py-2">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {pecas.map((peca, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{peca.estoque.nome}</td>
                      <td className="text-center py-2">{peca.quantidade}</td>
                      {canViewFinance && <td className="text-right py-2">{currency(peca.valor || 0)}</td>}
                      {canViewFinance && (
                        <td className="text-right py-2">
                          {currency((peca.valor || 0) * (peca.quantidade || 0))}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Total */}
        {canViewFinance && <Card className="p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Valor Total:</span>
            <span className="text-2xl font-bold text-blue-600">
              {currency(calcularTotal())}
            </span>
          </div>
        </Card>}

        {/* Observações */}
        {os.observacoes && (
          <Card className="p-4 mb-6">
            <h3 className="font-semibold mb-3">Observações</h3>
            <p className="text-sm text-gray-700">{os.observacoes}</p>
          </Card>
        )}

        {/* Rodapé do PDF */}
        <div className="border-t border-gray-200 pt-4 mt-8">
          <div className="text-center text-sm text-gray-600">
            <p>Desenvolvido por GDN Corp</p>
            <p>www.gdncorp.com.br</p>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/ordens')}>
          <ArrowLeft size={16} className="mr-2" />
          Voltar
        </Button>
        
        <div className="flex gap-2">
          {canViewFinance && (
            <Button onClick={gerarPDF}>
              <Download size={16} className="mr-2" />
              Gerar PDF
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/ordens/${id}/edit`)}>
            <Edit size={16} className="mr-2" />
            Editar
          </Button>
        </div>
      </div>

      {/* Check-in Modal */}
      {showCheckinModal && (
        <Modal
          isOpen={showCheckinModal}
          onClose={() => setShowCheckinModal(false)}
          title="Check-in Digital"
          size="large"
        >
          {checkinCompleted ? (
            <CheckinReceipt
              checkin={checkinCompleted}
              os={os}
              cliente={os.clientes}
              veiculo={os.veiculos}
              onClose={() => {
                setShowCheckinModal(false);
                setCheckinCompleted(null);
              }}
            />
          ) : (
            <CheckinForm
              osId={os.id}
              clienteId={os.cliente_id}
              veiculoId={os.veiculo_id}
              onComplete={(checkin) => {
                setCheckinCompleted(checkin);
              }}
              onCancel={() => setShowCheckinModal(false)}
            />
          )}
        </Modal>
      )}

      {/* Vehicle Timeline */}
      {os && os.veiculo_id && planFeatures.timeline && (
        <div className="mt-6">
          <VehicleTimeline 
            veiculoId={os.veiculo_id}
            clienteId={os.cliente_id}
          />
        </div>
      )}

      {/* Quote Modal */}
      {showQuoteModal && (
        <Modal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          title="Orçamento Digital"
          size="large"
        >
          <QuoteForm
            osId={os.id}
            clienteId={os.cliente_id}
            veiculoId={os.veiculo_id}
            onQuoteCreated={() => {
              setShowQuoteModal(false);
              loadOS();
            }}
            onCancel={() => setShowQuoteModal(false)}
          />
        </Modal>
      )}
    </div>
  );
}

